import { Link } from '@remix-run/react';
import { useLocale } from 'remix-i18next/react';
import LanguagePicker from '#common/components/LanguagePicker.tsx';
import { MonochromeCrispyVoiceLogo } from '#common/components/MonochromeCrispyVoiceLogo';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useTranslatedPath } from '#common/utils/i18n.tsx';

interface Voice {
    slug: string;
    name: string | null;
    voiceDescription: string | null;
    imageFileName: string | null;
}

interface Props {
    voices: Voice[];
    baseUrl: string;
    speakerSlug: string | null;
    speakerDisplayName: string | null;
}

export function LandingPage({ voices, baseUrl, speakerSlug, speakerDisplayName }: Props) {
    const locale = useLocale();
    const translatePath = useTranslatedPath(locale);
    const { t } = useEditableTranslation('common');

    return (
        <div className='min-h-screen flex flex-col'>
            {/* Hero */}
            <div className='bg-gradient-to-b from-crispybluedark-700 to-crispybluedark-600 text-white pt-12 pb-16 md:pt-16 md:pb-24 px-4'>
                <div className='max-w-5xl mx-auto'>
                    <div className='flex justify-end mb-6 text-sm font-semibold tracking-wide text-white/80'>
                        <LanguagePicker baseUrl={baseUrl} className='text-right' />
                    </div>
                    <div className='text-center'>
                        <MonochromeCrispyVoiceLogo className='h-12 md:h-16 mb-8 mx-auto opacity-60' />
                        {speakerDisplayName && (
                            <h1 className='font-display text-4xl md:text-6xl font-extrabold mb-4 tracking-tight'>
                                {speakerDisplayName}
                            </h1>
                        )}
                        <p className='text-lg md:text-xl text-white/80 max-w-2xl mx-auto'>{t('landing.subtitle')}</p>
                    </div>
                </div>
            </div>

            {/* Voice cards */}
            <div className='flex-1 bg-slate-50 px-4 py-12 md:py-16'>
                <div className='max-w-5xl mx-auto'>
                    <h2 className='text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10'>
                        {t('landing.title')}
                    </h2>
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8'>
                        {voices.map((voice) => {
                            return (
                                <Link
                                    key={voice.slug}
                                    to={translatePath(`/${voice.slug}`)}
                                    className='group block rounded-2xl bg-white shadow-sm border border-slate-200 overflow-hidden transition-all hover:shadow-xl hover:border-crispybluedark-400 hover:scale-[1.02]'
                                >
                                    {voice.imageFileName && (
                                        <div className='w-full aspect-[4/3] overflow-hidden bg-slate-100'>
                                            <img
                                                src={`/_/speakers/${voice.imageFileName}`}
                                                alt={voice.name ?? voice.slug}
                                                className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-300'
                                            />
                                        </div>
                                    )}
                                    <div className='p-5'>
                                        <h3 className='text-xl font-bold text-slate-900 group-hover:text-crispybluedark-600 transition-colors'>
                                            {voice.name ?? voice.slug}
                                        </h3>
                                        {voice.voiceDescription && (
                                            <p className='mt-2 text-sm text-slate-500 line-clamp-3'>
                                                {voice.voiceDescription}
                                            </p>
                                        )}
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className='bg-crispybluedark-700 py-8 text-center text-sm text-white/60'>
                <div className='flex justify-center gap-6'>
                    <Link to={translatePath('/gtc')} className='hover:text-white transition-colors'>
                        {t('footer:gtc')}
                    </Link>
                    <Link to={translatePath('/imprint')} className='hover:text-white transition-colors'>
                        {t('footer:imprint')}
                    </Link>
                </div>
                <p className='mt-3'>{t('footer:copyright', { year: new Date().getFullYear() })}</p>
                <div className='mt-4'>
                    <MonochromeCrispyVoiceLogo className='h-8 mx-auto opacity-40' />
                </div>
            </footer>
        </div>
    );
}
